import { useState } from "react";
import { createFileRoute, redirect, useNavigate, useRouter } from "@tanstack/react-router";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useQuery } from "@tanstack/react-query";
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import Divider from "@mui/material/Divider";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { API_BASE } from "@/lib/http";
import { authApi } from "@/api/auth";
import { tokenStore } from "@/auth/token-store";
import { markSignedIn } from "@/auth/session";
import { useSessionStore } from "@/stores/session";
import { Form } from "@/components/form/form";
import { FormTextField } from "@/components/form/fields";

export const Route = createFileRoute("/_auth/login")({
  validateSearch: (s: Record<string, unknown>): { redirect?: string } =>
    typeof s.redirect === "string" ? { redirect: s.redirect } : {},
  beforeLoad: async () => {
    const { needsSetup } = await authApi.setupStatus();
    if (needsSetup) throw redirect({ to: "/setup" });
  },
  component: LoginPage,
});

const credsSchema = z.object({
  email: z.string().email("Enter a valid email"),
  password: z.string().min(1, "Required"),
});
const mfaSchema = z.object({ code: z.string().min(1, "Required") });

function LoginPage() {
  const { redirect } = Route.useSearch();
  const navigate = useNavigate();
  const router = useRouter();
  const setUser = useSessionStore((s) => s.setUser);

  const [error, setError] = useState<string | null>(null);
  const [mfaToken, setMfaToken] = useState<string | null>(null);
  const [useBackup, setUseBackup] = useState(false);

  const oidc = useQuery({ queryKey: ["sso", "oidc"], queryFn: authApi.oidcProviders });
  const saml = useQuery({ queryKey: ["sso", "saml"], queryFn: authApi.samlProviders });

  const credsForm = useForm<z.infer<typeof credsSchema>>({
    resolver: zodResolver(credsSchema),
    defaultValues: { email: "", password: "" },
  });
  const mfaForm = useForm<z.infer<typeof mfaSchema>>({
    resolver: zodResolver(mfaSchema),
    defaultValues: { code: "" },
  });

  async function finish(res: { accessToken: string; refreshToken: string; user?: unknown }) {
    tokenStore.set(res.accessToken, res.refreshToken);
    markSignedIn();
    setUser(null); // force the guard to re-hydrate /auth/me
    await router.invalidate();
    navigate({ to: redirect ?? "/" });
  }

  async function onCreds(values: z.infer<typeof credsSchema>) {
    setError(null);
    try {
      const result = await authApi.login(values.email, values.password);
      if ("mfaRequired" in result) {
        setMfaToken(result.mfaToken);
      } else {
        await finish(result);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : "Sign-in failed");
    }
  }

  async function onMfa(values: z.infer<typeof mfaSchema>) {
    if (!mfaToken) return;
    setError(null);
    try {
      const res = await authApi.mfaComplete(
        mfaToken,
        useBackup ? undefined : values.code,
        useBackup ? values.code : undefined,
      );
      await finish(res);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Verification failed");
    }
  }

  const ssoStart = (kind: "oidc" | "saml", name: string) => {
    globalThis.location.assign(`${API_BASE}/auth/${kind}/${encodeURIComponent(name)}/start`);
  };

  return (
    <Stack spacing={2.5}>
      <Typography variant="h2">Sign in to UDIP</Typography>
      {error ? <Alert severity="error">{error}</Alert> : null}

      {mfaToken ? (
        <Form form={mfaForm} onSubmit={onMfa}>
          <Stack spacing={2}>
            <Typography variant="body2" color="text.secondary">
              Enter your {useBackup ? "backup code" : "6-digit authenticator code"}.
            </Typography>
            <FormTextField
              control={mfaForm.control}
              name="code"
              label={useBackup ? "Backup code" : "Code"}
            />
            <Button type="submit" variant="contained" disabled={mfaForm.formState.isSubmitting}>
              Verify
            </Button>
            <Button size="small" onClick={() => setUseBackup((v) => !v)}>
              {useBackup ? "Use authenticator code" : "Use a backup code"}
            </Button>
          </Stack>
        </Form>
      ) : (
        <Form form={credsForm} onSubmit={onCreds}>
          <Stack spacing={2}>
            <FormTextField control={credsForm.control} name="email" label="Email" type="email" />
            <FormTextField
              control={credsForm.control}
              name="password"
              label="Password"
              type="password"
            />
            <Button type="submit" variant="contained" disabled={credsForm.formState.isSubmitting}>
              Sign in
            </Button>
          </Stack>
        </Form>
      )}

      {!mfaToken && (oidc.data?.length || saml.data?.length) ? (
        <>
          <Divider>or</Divider>
          <Stack spacing={1}>
            {oidc.data?.map((p) => (
              <Button
                key={`o-${p.name}`}
                variant="outlined"
                onClick={() => ssoStart("oidc", p.name)}
              >
                {p.displayName ?? p.name}
              </Button>
            ))}
            {saml.data?.map((p) => (
              <Button
                key={`s-${p.name}`}
                variant="outlined"
                onClick={() => ssoStart("saml", p.name)}
              >
                {p.displayName ?? p.name}
              </Button>
            ))}
          </Stack>
        </>
      ) : null}
    </Stack>
  );
}
